import { useMemo, useRef } from 'react';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import {
  buildUsageAnalysis,
  COMPARISON_WINDOW,
  type UsageAnalysis,
} from '@/services/org-admin/analysis';
import { isMonthSelection, type UsageWindowSelection } from '@/services/org-admin';
import { useOrgUsageReport } from './useOrgUsageReport';
import { useOrgUsageServiceReport } from './useOrgUsageServiceReport';
import { useOrgUsageTimeseries } from './useOrgUsageTimeseries';

/**
 * The Analysis view's single data dependency.
 *
 * Composes existing queries — no new endpoints. The window report and service report are the same
 * keys the Usage tab uses, so moving between the two tabs at one window is served from cache.
 *
 * The third query is the interesting one: it fetches the NEXT-LARGER window purely so the adapter can
 * subtract and obtain the preceding period. That is the only way to answer "is spend up or down?"
 * against an API that returns cumulative window totals and no history — and it is real data, which
 * beats a plausible placeholder for the single most consequential number on the page.
 *
 * `isFetching` is exposed separately from `isLoading` so a window change dims the existing charts
 * instead of replacing them with skeletons: a chart that vanishes and returns reads as broken.
 */
export interface UseUsageAnalysisResult {
  data: UsageAnalysis | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useUsageAnalysis(
  selection: UsageWindowSelection,
  options?: { enabled?: boolean },
): UseUsageAnalysisResult {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const enabled = options?.enabled ?? true;

  // Every one of these accepts a day window OR a calendar month, so nothing here branches on mode.
  const report = useOrgUsageReport(selection, { enabled });
  const serviceReport = useOrgUsageServiceReport(selection, { enabled });
  /*
   * The trend's data, and the reason there is no longer a fixture behind it.
   *
   * Deliberately excluded from `isError` below: a backend without this endpoint 404s, and that is a
   * missing chart, not a broken page — the other three quarters of the view are still correct.
   */
  const timeseries = useOrgUsageTimeseries(selection, { enabled });

  /*
   * The comparison report exists only for a DAY window, which no surface selects any more.
   *
   * KEPT, NOT DELETED, and this is the reason. Both tabs now pass a calendar month, so this query is
   * permanently disabled and `SpendSummaryPanel` permanently renders "Not compared to an earlier
   * period". That is a real gap, not a design: the hero figure on the Usage tab is the one number
   * most worth a delta. The month-over-month recipe already exists and is proven — Overview derives
   * exactly that in `buildOverviewForMonth` via `changeVsPreviousMonth`, normalising by days elapsed
   * because `current` is partial and `previous` is closed. Wiring it here means fetching `previous`
   * alongside the selection and calling that helper; deleting this scaffolding first would mean
   * rebuilding `SpendComparison`, `DeltaBadge` and this hook's gating to do it.
   *
   * So: unreachable today, deliberately, with the follow-up named.
   *
   * It fetches the NEXT-LARGER day window purely so the adapter can subtract and obtain the
   * preceding period. A calendar month has no such nesting, so in month mode this query is disabled
   * outright and the adapter returns `comparison: null` — which the summary states in words rather
   * than showing a dash. Hooks cannot be called conditionally, so it is always mounted and gated by
   * `enabled`, the same pattern the rest of this module uses.
   */
  const isMonth = isMonthSelection(selection);
  const comparisonWindow = isMonth ? null : COMPARISON_WINDOW[selection];
  const comparisonReport = useOrgUsageReport(comparisonWindow ?? 30, {
    enabled: enabled && comparisonWindow !== null,
  });

  /*
   * "Now" is captured once per mount rather than read inside the adapter.
   *
   * The adapter is pure by design, and both the cycle projection and the placeholder trend derive
   * from this value — reading the clock every render would shift the projection and the x-axis
   * mid-session and make the view impossible to compare against a screenshot.
   */
  const nowRef = useRef<Date>();
  if (!nowRef.current) nowRef.current = new Date();

  const data = useMemo(() => {
    // Nothing to build from yet: `null` lets the view show its loading state rather than a fully
    // zeroed analysis, which would read as "your org used nothing".
    if (!report.data && !serviceReport.data) return null;
    return buildUsageAnalysis({
      selection,
      report: report.data,
      comparisonReport: comparisonWindow === null ? undefined : comparisonReport.data,
      serviceReport: serviceReport.data,
      timeseries: timeseries.data,
      orgId,
      now: nowRef.current!,
    });
  }, [
    selection,
    report.data,
    comparisonReport.data,
    comparisonWindow,
    serviceReport.data,
    timeseries.data,
    orgId,
  ]);

  return {
    data,
    isLoading: (report.isLoading || serviceReport.isLoading) && !data,
    // The comparison query is deliberately excluded: the page must not sit dimmed waiting on a
    // secondary figure when the primary ones have arrived.
    isFetching: report.isFetching || serviceReport.isFetching,
    // Both primary payloads are needed for a complete picture, but one failing should not blank the
    // view — the adapter tolerates either being absent, so this is a hard error only when BOTH fail.
    isError: report.isError && serviceReport.isError,
    error: report.error ?? serviceReport.error,
    refetch: () => {
      void report.refetch();
      void serviceReport.refetch();
      void timeseries.refetch();
      if (comparisonWindow !== null) void comparisonReport.refetch();
    },
  };
}
